import { FeatureFlagName } from '@/config/featureFlags'
import { FeatureFlagsContext } from '@/shared/contexts/featureFlagsContext'
import { useWorkspace } from '@/shared/hooks/data-hooks/workspaces/useWorkspace'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import { SubscriptionPlanType } from '@/types/subscription'
import { useContext } from 'react'

export const useFeatureFlags = (flagNames: FeatureFlagName[]): boolean[] => {
  const context = useContext(FeatureFlagsContext)

  if (!context) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider'
    )
  }

  return flagNames.map((flagName) => {
    const featureFlag = context.featureFlags.find(
      (flag) => flag.name === flagName
    )
    if (
      featureFlag?.requiredSubscriptions &&
      featureFlag.requiredSubscriptions.length > 0
    ) {
      throw new Error(
        `Reading ${flagName} requires useFeatureFlagsWithWorkspace`
      )
    }

    return featureFlag?.enabled ?? false
  })
}

export const useFeatureFlagsWithSubscription = (
  flagNames: FeatureFlagName[],
  subscriptionType?: SubscriptionPlanType
): boolean[] => {
  const context = useContext(FeatureFlagsContext)

  if (!context) {
    throw new Error(
      'useFeatureFlagsWithSubscription must be used within a FeatureFlagsProvider'
    )
  }

  return flagNames.map((flagName) => {
    const feature = context.featureFlags.find((flag) => flag.name === flagName)

    if (!feature) {
      return false
    }

    if (!feature.enabled) {
      return false
    }

    if (
      !feature.requiredSubscriptions ||
      feature.requiredSubscriptions.length === 0
    ) {
      return true
    }

    if (!subscriptionType) {
      return false
    }

    return (feature.requiredSubscriptions ?? []).includes(subscriptionType)
  })
}

export const useFeatureFlagsWithWorkspace = (
  flagNames: FeatureFlagName[]
): { flags: boolean[]; isLoading: boolean } => {
  const { workspaceId } = useWorkspaceContext()
  const { data: { data: workspace } = {}, isLoading } =
    useWorkspace(workspaceId)

  const currentSub = workspace?.subscription?.subscriptionPlan?.type
  const flags = useFeatureFlagsWithSubscription(flagNames, currentSub)

  return { flags, isLoading }
}
